import { useState } from 'react';
import { ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip } from 'recharts';
import type { EngagementSnapshot } from '@/types/emotion';
import { clsx } from 'clsx';

type Props = {
  summary: Record<string, number> | null;  // For the Bar Chart
  latestSnapshot?: EngagementSnapshot | null; // For the Radar Chart (NEW)
};

const colors: Record<string, string> = {
  engaged: 'text-green-500',
  confused: 'text-orange-500',
  disengaged: 'text-red-500',
  neutral: 'text-text-dimmed'
};

// HELPER: Prepare Radar Data
const processRadarData = (data: EngagementSnapshot | null) => {
  if (!data) return [];
  return [
    { subject: 'Happy', A: (data.happy || 0) * 100, fullMark: 100 },
    { subject: 'Surprise', A: (data.surprise || 0) * 100, fullMark: 100 },
    { subject: 'Neutral', A: (data.neutral || 0) * 100, fullMark: 100 },
    { subject: 'Sad', A: (data.sad || 0) * 100, fullMark: 100 },
    { subject: 'Fear', A: (data.fear || 0) * 100, fullMark: 100 },
    { subject: 'Disgust', A: (data.disgust || 0) * 100, fullMark: 100 },
    { subject: 'Angry', A: (data.angry || 0) * 100, fullMark: 100 },
  ];
};

export const EngagementDistributionChart = ({ summary, latestSnapshot }: Props) => {
  const [mode, setMode] = useState<'distribution' | 'radar'>('distribution');
  const radarData = processRadarData(latestSnapshot);

  if (!summary) {
    return <div className="rounded-2xl border border-background-border bg-background-surface p-6 text-sm text-text-dimmed">No summary data</div>;
  }

  return (
    <div className="rounded-2xl border border-background-border bg-background-surface p-6 h-full flex flex-col">
      
      {/* HEADER + TABS */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-text-primary">
          {mode === 'distribution' ? 'Engagement Distribution' : 'Raw Tensor Output'}
        </p>
        
        <div className="flex bg-background-main p-1 rounded-lg border border-background-border">
           <button
             onClick={() => setMode('distribution')}
             className={clsx("px-3 py-1 text-[10px] font-medium rounded-md transition-all", mode === 'distribution' ? "bg-background-border text-text-primary" : "text-text-dimmed hover:text-text-primary")}
           >
             📊 Modes
           </button>
           <button
             onClick={() => setMode('radar')}
             className={clsx("px-3 py-1 text-[10px] font-medium rounded-md transition-all", mode === 'radar' ? "bg-brand/30 text-text-primary" : "text-text-dimmed hover:text-text-primary")}
           >
             🕸️ Tensor
           </button>
        </div>
      </div>

      {/* BODY: TAB CONTENT */}
      <div className="flex-1 min-h-[250px]">
        
        {/* TAB 1: EXISTING BAR CHART */}
        {mode === 'distribution' && (
          <div className="space-y-4 pt-2">
            {Object.entries(summary).map(([key, value]) => (
              <div key={key}>
                <div className="flex items-center justify-between text-xs text-text-primary mb-1">
                  <span className={`capitalize font-medium ${colors[key] ?? ''}`}>{key}</span>
                  <span className="font-mono">{value}%</span>
                </div>
                <div className="h-2 rounded-full bg-background-border overflow-hidden">
                  <div
                    className={clsx("h-full rounded-full transition-all duration-1000", 
                      key === 'engaged' ? 'bg-green-500' : 
                      key === 'confused' ? 'bg-orange-500' : 
                      key === 'disengaged' ? 'bg-red-500' : 'bg-text-dimmed'
                    )}
                    style={{ width: `${value}%` }}
                  />
                </div>
              </div>
            ))}
            <div className="mt-6 p-3 rounded bg-background-main/50 border border-background-border">
               <p className="text-[10px] text-text-dimmed leading-relaxed">
                 <span className="font-bold text-text-primary">Note:</span> These 4 states are calculated by mapping the 7 raw emotion tensors (Tab 2) onto the Circumplex Model of Affect.
               </p>
            </div>
          </div>
        )}

        {/* TAB 2: NEW RADAR CHART */}
        {mode === 'radar' && (
           <ResponsiveContainer width="100%" height="100%">
             <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
               <PolarGrid gridType="polygon" stroke="#27272A" />
               <PolarAngleAxis dataKey="subject" tick={{ fill: '#A1A1AA', fontSize: 10 }} />
               <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
               <Radar
                 name="Confidence"
                 dataKey="A"
                 stroke="#8F00FF"
                 strokeWidth={2}
                 fill="#8F00FF"
                 fillOpacity={0.3}
               />
               <Tooltip 
                 contentStyle={{ backgroundColor: '#18181B', borderColor: '#27272A', color: '#F4F4F5', fontSize: '12px' }}
                 formatter={(value: number) => [`${Math.round(value)}%`, 'Probability']}
               />
             </RadarChart>
           </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};